import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Globe, Heart, Loader2, MessageCircle, MessageSquare, Play } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { B, mediaSrc } from "./common";
import { Avatar } from "./PostCard";
import { FotoEdit } from "./AuthorDialog";
import { COPPER } from "../VestaAuth";
import { ProfiloExtra } from "@/components/AiProfiloCrm";
import { Cover, Badges, Works } from "./WebExtra";

const Stat = ({ v, l, testid }) => (
  <div className="text-center" data-testid={testid}>
    <div className="font-heading text-lg font-extrabold text-white">{v}</div>
    <div className="text-[11px] text-[#9CA3AF]">{l}</div>
  </div>
);

function Header({ d, setD, onMessage }) {
  const p = d.partner;
  return (
    <section className="px-4 pt-5" data-testid="profilo-header">
      <div className="flex items-center gap-5">
        <Avatar a={{ ...d, partner_name: p?.name }} foto={d.foto} logo={p?.has_logo ? `/api/vesta/bacheca/logo/${p.id}` : null} size={86} testid="profilo-avatar" />
        <div className="flex flex-1 justify-around">
          <Stat v={d.posts_count} l="post" testid="profilo-stat-post" />
          <Stat v={<span className="inline-flex items-center gap-1"><img src="/vesta-coin-rame.png" alt="" className="h-4 w-4" />{d.coins || 0}</span>} l="Vesta Coin" testid="profilo-stat-coin" />
        </div>
      </div>
      <div className="mt-3 space-y-0.5">
        <h1 className="font-heading text-base font-extrabold" data-testid="profilo-name">{d.staff ? d.name : p?.name || d.name}</h1>
        <div className="text-xs text-[#9CA3AF]" data-testid="profilo-sub">{d.staff ? "Team Torri Solar" : [d.name, p?.city, p?.province && `(${p.province})`].filter(Boolean).join(" · ")}</div>
        {p?.descrizione && <p className="whitespace-pre-line pt-1 text-sm text-[#D1D5DB]">{p.descrizione}</p>}
        <ProfiloExtra p={p} dark />
        <Badges p={p} />
        {p?.website && <a href={/^https?:\/\//.test(p.website) ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 pt-1 text-sm underline" style={{ color: COPPER }}><Globe className="h-4 w-4" />{p.website}</a>}
      </div>
      <div className="mt-4">
        {d.mine ? <FotoEdit d={d} setD={setD} /> : (
          <button type="button" onClick={onMessage} data-testid="profilo-message"
            className="flex w-full items-center justify-center gap-2 rounded-lg py-2 text-sm font-bold text-white transition-transform active:scale-[.98]" style={{ background: COPPER }}>
            <MessageSquare className="h-4 w-4" /> Scrivi in privato
          </button>
        )}
      </div>
    </section>
  );
}

const Tile = ({ it, onOpen }) => (
  <button type="button" onClick={() => onOpen(it.id)} className="group relative aspect-square overflow-hidden bg-[#16191E]" data-testid={`profilo-tile-${it.id}`}>
    {it.kind === "video" ? <video src={mediaSrc(it.cover)} muted playsInline preload="metadata" className="h-full w-full object-cover" />
      : <img src={mediaSrc(it.cover)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />}
    {it.kind === "video" ? <Play className="absolute right-1.5 top-1.5 h-4 w-4 fill-white text-white drop-shadow" /> : it.n > 1 && <Copy className="absolute right-1.5 top-1.5 h-4 w-4 text-white drop-shadow" />}
    <span className="absolute inset-0 flex items-center justify-center gap-4 bg-black/50 text-sm font-bold text-white opacity-0 transition-opacity group-hover:opacity-100">
      <span className="flex items-center gap-1"><Heart className="h-4 w-4 fill-white" />{it.likes}</span>
      <span className="flex items-center gap-1"><MessageCircle className="h-4 w-4 fill-white" />{it.comments}</span>
    </span>
  </button>
);

function Grid({ uid, onOpen }) {
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { setG(null); api.get(`${B}/autore/${uid}/griglia`).then((r) => setG(r.data)).catch(() => setG({ items: [] })); }, [uid]);
  const more = useCallback(async () => {
    if (busy || !g?.has_more) return;
    setBusy(true);
    try { const r = await api.get(`${B}/autore/${uid}/griglia`, { params: { before: g.items[g.items.length - 1].created_at } }); setG({ items: [...g.items, ...r.data.items], has_more: r.data.has_more }); } catch { /* retry on next scroll */ }
    setBusy(false);
  }, [busy, g, uid]);
  useEffect(() => {
    const el = end.current; if (!el) return;
    const ob = new IntersectionObserver((e) => e[0].isIntersecting && more(), { rootMargin: "400px" });
    ob.observe(el); return () => ob.disconnect();
  }, [more]);
  if (!g) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /></div>;
  if (!g.items.length) return <p className="py-14 text-center text-sm text-[#6B7280]" data-testid="profilo-grid-empty">Nessuna foto pubblicata</p>;
  return (
    <>
      <div className="grid grid-cols-3 gap-0.5" data-testid="profilo-grid">{g.items.map((it) => <Tile key={it.id} it={it} onOpen={onOpen} />)}</div>
      <div ref={end} className="h-1" />
      {busy && <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" style={{ color: COPPER }} /></div>}
    </>
  );
}

export default function ProfiloPage() {
  const { uid } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`${B}/autore/${uid}`).then((r) => setD(r.data)).catch(() => { toast.error("Profilo non disponibile"); nav("/vesta/bacheca"); });
  }, [uid, nav]);
  return (
    <div className="min-h-screen bg-[#0D0F12] pb-16 text-white" data-testid="vesta-profilo-page">
      <header className="sticky top-0 z-40 border-b border-[#2A303C] bg-[#0D0F12]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-[600px] items-center gap-2 px-3">
          <Link to="/vesta/bacheca" className="rounded-full p-2 hover:bg-[#1F242D]" data-testid="profilo-back"><ArrowLeft className="h-5 w-5" /></Link>
          <span className="truncate font-heading text-lg font-bold" data-testid="profilo-title">{d ? (d.staff ? d.name : d.partner?.name || d.name) : "Profilo"}</span>
        </div>
      </header>
      <main className="mx-auto max-w-[600px]">
        {!d ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: COPPER }} /></div> : (
          <>
            <Cover p={d.partner} />
            <Header d={d} setD={setD} onMessage={() => nav(`/vesta/bacheca?dm=${uid}`)} />
            <Works p={d.partner} />
            <div className="mt-5 border-t border-[#2A303C]" />
            <Grid uid={uid} onOpen={(id) => nav(`/vesta/bacheca?post=${id}`)} />
          </>
        )}
      </main>
    </div>
  );
}
